import { protocolsSteps, SUBMIT_PROTOCOL_LABEL } from "./constants";
import { type biologicalsInterface } from "./shared/interfaces/product-interfaces";
import { type ProtocolStepItemType } from "./types";

export const getItems = (): Array<{
  key: string;
  title: string;
  path: string;
  stepNumber: number;
}> => {
  return protocolsSteps.map((item: ProtocolStepItemType) => ({
    key: item.title,
    title: item.title,
    path: item.path,
    stepNumber: item.stepNumber,
  }));
};

export const getCancelButtonLabel = (current: number): string => {
  if (current === 1) return "Cancel";
  return "Back";
};

export const getNextButtonLabel = (current: number): string => {
  if (current === protocolsSteps.length) return SUBMIT_PROTOCOL_LABEL;
  return protocolsSteps[current].title;
};

export const nextStep = (step: string): any => {
  const currentStep = protocolsSteps.find((s) => s.path === step);
  if (currentStep && currentStep.stepNumber !== 5) {
    const nextStepNumber = currentStep.stepNumber + 1;
    const nextStep = protocolsSteps.find(
      (step) => step.stepNumber === nextStepNumber
    );

    return nextStep;
  }
  return undefined;
};

export const prevStep = (step: string): any => {
  const currentStep = protocolsSteps.find((s) => s.path === step);
  if (currentStep && currentStep.stepNumber !== 1) {
    const prevStepNumber = currentStep.stepNumber - 1;
    const prevStep = protocolsSteps.find(
      (step) => step.stepNumber === prevStepNumber
    );
    return prevStep;
  }
  return undefined;
};

export const getCurrentStepNumber = (step: string): number => {
  const currentStep = protocolsSteps.find((s) => s.path === step);
  if (currentStep) return currentStep.stepNumber;
  return 1;
};

export const formatCropsData = (cropsData: any): any[] => {
  if (!cropsData) return [];
  const formattedCropsData = cropsData?.map((x: any) => {
    // varieties_ids is populated separately via a POST /catalog/seeds/query
    // batch call (see fetchCropVarietiesAvailability in BasicInfoComponent),
    // since /v2/catalog/crops itself doesn't return it. If that call fails,
    // the field is left absent -- treat "unknown" as "has varieties" rather
    // than "zero, disabled"; only a confirmed empty array disables the option.
    const varietiesIds = x?.varieties_ids;
    const hasVarietiesData = Array.isArray(varietiesIds);
    const hasVarieties = hasVarietiesData ? varietiesIds.length > 0 : true;
    return {
      value: x?.id,
      label: x?.name,
      varietiesLength: varietiesIds?.length || 0,
      hasVarieties,
      disabled: !hasVarieties,
    };
  });
  return formattedCropsData;
};

// Maps a /v2/catalog/crops CropDTO list (id, name[] per language,
// localized_crops[] per country) into the { id, name } shape the crop
// selection flow already expects, scoped to a single country's localized
// crop id (needed downstream to query /catalog/seeds/query).
export const mapCatalogCropsToLegacyShape = (
  catalogCrops: any,
  country: string
): any[] => {
  if (!Array.isArray(catalogCrops)) return [];
  const normalizedCountry = (country ?? "").toUpperCase();
  return catalogCrops
    .map((crop: any) => {
      const localizedCrop = crop?.localized_crops?.find(
        (lc: any) => (lc?.location ?? "").toUpperCase() === normalizedCountry
      );
      if (!localizedCrop?.id) return null;
      const name =
        crop?.name?.find((n: any) => n?.language === "en")?.name ??
        crop?.name?.[0]?.name ??
        "";
      return { id: localizedCrop.id, name };
    })
    .filter(Boolean);
};

// Groups seed ids returned by POST /catalog/seeds/query by the localized
// crop id (SeedVarietyDTO.localized_crop_info.id) they belong to, so each
// crop option can report whether it has any seed varieties for the country.
export const groupSeedIdsByLocalizedCropId = (
  seeds: any
): Record<string, string[]> => {
  if (!Array.isArray(seeds)) return {};
  return seeds.reduce((acc: Record<string, string[]>, seed: any) => {
    const localizedCropId = seed?.localized_crop_info?.id;
    if (!localizedCropId || !seed?.id) return acc;
    (acc[localizedCropId] ??= []).push(seed.id);
    return acc;
  }, {});
};

export const formatDate = (creationDate: string): string => {
  const dateObj = new Date(creationDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  // Format the date
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const getDefaultActiveTabs = (protocolType: string[]): string => {
  // Explicit priority order
  if (protocolType.includes("seed")) return "seed";
  if (protocolType.includes("biological")) return "biological";
  if (protocolType.includes("nutrient")) return "nutrient";
  return "seed";
};

/**
 * Converts a protocol type to an array of strings.
 * @param protocolType The protocol type to convert.
 * @returns An array of protocol types as strings.
 */
export const getProtocolTypes = (protocolType: unknown): string[] => {
  if (typeof protocolType === "string") {
    return protocolType
      .split(",")
      .map((type) => type.trim())
      .filter(Boolean);
  }

  if (Array.isArray(protocolType)) {
    return protocolType
      .filter((type): type is string => typeof type === "string")
      .map((type) => type.trim())
      .filter(Boolean);
  }

  return [];
};

export const isBiologicalHasValidData = (
  biologicals: biologicalsInterface
): boolean | undefined => {
  const treatments = biologicals?.treatments;
  if (!treatments || treatments.length === 0) return false;

  const toNumber = (value: any): number | null => {
    const parsed =
      typeof value === "number"
        ? value
        : typeof value === "string"
        ? Number(value)
        : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  };

  const allTreatmentsValid = treatments.every((treatment: any) => {
    const hasProduct =
      Array.isArray(treatment?.products) && treatment.products.length > 0;
    const hasInstructions =
      typeof treatment?.instructions === "string" &&
      treatment.instructions.trim().length > 0;

    const min = toNumber(treatment?.tank_mix_rate?.min);
    const max = toNumber(treatment?.tank_mix_rate?.max);

    const hasTankMixRate =
      min !== null && max !== null && min > 0 && max > 0 && min <= max;

    return hasProduct && hasTankMixRate && hasInstructions;
  });

  const applications = biologicals?.applications ?? [];

  const noApplicationError = applications.every(
    (app) => (app?.description ?? "").trim().length > 0
  );

  return noApplicationError && allTreatmentsValid;
};

const parseFiniteNumber = (value: any): number | null => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : NaN;

  return Number.isFinite(parsed) ? parsed : null;
};

const hasValidDensityOptions = (options: any): boolean => {
  if (!Array.isArray(options) || options.length === 0) return false;
  return options.some((option) => parseFiniteNumber(option) !== null);
};

const hasValidDensityRange = (range: any): boolean => {
  if (!range) return false;

  const min = parseFiniteNumber(range?.min);
  const max = parseFiniteNumber(range?.max);

  if (min === null || max === null) return false;
  if (min <= 0 || max <= 0) return false;

  return min <= max;
};

export const isSeedHasValidData = (formValidationData: any): boolean => {
  const products = Array.isArray(formValidationData?.products)
    ? formValidationData.products
    : [];

  if (products.length === 0 || !formValidationData?.measure_unit) return false;

  const densityMode = formValidationData?.globalDensityMode ?? "range";
  const isGlobalDensity =
    formValidationData?.globalDensityRange === true ||
    formValidationData?.globalDensityRange === "true" ||
    formValidationData?.globalDensityRange === 1;

  if (isGlobalDensity) {
    if (densityMode === "options") {
      const globalOptionsValid = hasValidDensityOptions(
        formValidationData?.globalDensityOptions
      );

      const perProductOptionsValid = products.every((product: any) =>
        hasValidDensityOptions(product?.density_options)
      );

      return globalOptionsValid || perProductOptionsValid;
    }

    return hasValidDensityRange(formValidationData?.density_range);
  }

  if (densityMode === "options") {
    return products.every((product: any) =>
      hasValidDensityOptions(product?.density_options)
    );
  }

  return products.every((product: any) =>
    hasValidDensityRange(product?.density_range)
  );
};

// ...existing code...

// Unit conversion map - single source of truth for all unit conversions
const UNIT_MAP: Record<string, { withHa: string; short: string }> = {
  liters: { withHa: "L/ha", short: "L" },
  kilograms: { withHa: "kg/ha", short: "kg" },
  units: { withHa: "pcs/ha", short: "pcs" },
  boxes: { withHa: "box/ha", short: "box" },
  sack: { withHa: "sck/ha", short: "sck" },
  gallon: { withHa: "gal/ha", short: "gal" },
  grams: { withHa: "g/ha", short: "g" },
  tonnes: { withHa: "t/ha", short: "t" },
  milliliter: { withHa: "mL/ha", short: "mL" },
  milliliters: { withHa: "mL/ha", short: "mL" },
  meters: { withHa: "m/ha", short: "m" },
  ounces: { withHa: "oz/ha", short: "oz" },
  pounds: { withHa: "lbs/ha", short: "lbs" },
  pints: { withHa: "pt/ha", short: "pt" },
  dose: { withHa: "doses/ha", short: "doses" },
  quarts: { withHa: "qt/ha", short: "qt" },
  unknown: { withHa: "Unknown", short: "Unknown" },
} as const;

const DEFAULT_UNIT = { withHa: "L/ha", short: "L" } as const;

export const getProductUnit = (unit: string | undefined): string => {
  if (!unit) return DEFAULT_UNIT.withHa;
  const normalized = unit.toLowerCase().trim();
  return UNIT_MAP[normalized]?.withHa ?? DEFAULT_UNIT.withHa;
};

/**
 * Restricts input to numbers only with a maximum of two decimal places
 * @param event - Keyboard event from input
 * @param currentValue - Current value of the input field
 * @returns void - prevents default behavior if input is invalid
 */
export const restrictToTwoDecimals = (
  event: React.KeyboardEvent<HTMLInputElement>,
  currentValue?: string | number | null
): void => {
  const key = event.key;

  // Try multiple ways to get the input element for Ant Design compatibility
  let input: HTMLInputElement | null = null;

  if ((event.target as HTMLElement).tagName === "INPUT") {
    input = event.target as HTMLInputElement;
  } else if ((event.currentTarget as HTMLElement).tagName === "INPUT") {
    input = event.currentTarget as HTMLInputElement;
  } else {
    // Try to find input in the event path
    const path = event.nativeEvent.composedPath?.() ?? [];
    for (const element of path) {
      if ((element as HTMLElement).tagName === "INPUT") {
        input = element as HTMLInputElement;
        break;
      }
    }
  }

  // If we still can't find the input, try querySelector
  if (!input && event.currentTarget) {
    input = (event.currentTarget as HTMLElement).querySelector("input");
  }

  const inputValue = input?.value ?? "";
  const selectionStart = input?.selectionStart ?? 0;
  const selectionEnd = input?.selectionEnd ?? 0;
  const hasSelection = selectionStart !== selectionEnd;

  // Allow: backspace, delete, tab, escape, enter, arrow keys
  if (
    key === "Backspace" ||
    key === "Delete" ||
    key === "Tab" ||
    key === "Escape" ||
    key === "Enter" ||
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "ArrowUp" ||
    key === "ArrowDown"
  ) {
    return;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  // Only allow numbers and one decimal point
  if (!/^\d$/.test(key) && key !== ".") {
    event.preventDefault();
    return;
  }

  // Prevent multiple decimal points
  if (key === "." && inputValue.includes(".")) {
    event.preventDefault();
    return;
  }

  // Check decimal places restriction
  if (inputValue.includes(".")) {
    const decimalIndex = inputValue.indexOf(".");
    const decimalPart = inputValue.split(".")[1];

    // If there are already 2 decimal digits
    if (decimalPart && decimalPart.length >= 2) {
      // If cursor is after the decimal point and no text is selected, prevent input
      if (selectionStart > decimalIndex && !hasSelection) {
        event.preventDefault();
      }
    }
  }
};

/**
 * Restricts input to integers only (no decimal points)
 * @param event - Keyboard event from input
 * @param currentValue - Current value of the input field (optional)
 * @returns void - prevents default behavior if input is invalid
 */
export const restrictToIntegersOnly = (
  event: React.KeyboardEvent<HTMLInputElement>,
  currentValue?: string | number | null
): void => {
  const key = event.key;

  // Allow: backspace, delete, tab, escape, enter, arrow keys
  if (
    key === "Backspace" ||
    key === "Delete" ||
    key === "Tab" ||
    key === "Escape" ||
    key === "Enter" ||
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "ArrowUp" ||
    key === "ArrowDown"
  ) {
    return;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
  if (event.ctrlKey || event.metaKey) {
    return;
  }

  // Only allow numbers (no decimal point)
  if (!/^\d$/.test(key)) {
    event.preventDefault();
  }
};
